import React, { useState } from 'react';
import styled from 'styled-components';
import { useInventory } from '../../contexts/InventoryContext';
import InventoryDashboard from './InventoryDashboard';
import InventoryItemList from './InventoryItemList';
import AddInventoryModal from './AddInventoryModal';
import MovementHistory from './MovementHistory';
import SyncStatus from './SyncStatus';

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
`;

const Title = styled.h1`
  color: #2c3e50;
  font-size: 28px;
  font-weight: 600;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;

  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    }
  }

  &.secondary {
    background: #f8f9fa;
    color: #495057;
    border: 1px solid #dee2e6;
    
    &:hover {
      background: #e9ecef;
    }
  }

  &.refresh {
    background: #17a2b8;
    color: white;
    
    &:hover {
      background: #138496;
    }
  }
`;

const TabContainer = styled.div`
  margin-bottom: 30px;
`;

const TabList = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e9ecef;
`;

const Tab = styled.button`
  padding: 12px 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 500;
  color: ${props => props.active ? '#667eea' : '#6c757d'};
  border-bottom: 3px solid ${props => props.active ? '#667eea' : 'transparent'};
  transition: all 0.3s ease;

  &:hover {
    color: #667eea;
    background: #f8f9fa;
  }
`;

const TabContent = styled.div`
  min-height: 400px;
`;

const StatusBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.inSync ? '#d4edda' : '#f8d7da'};
  color: ${props => props.inSync ? '#155724' : '#721c24'};
  padding: 8px 20px;
  text-align: center;
  font-weight: 500;
  transition: all 0.3s ease;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AlertBanner = styled.div`
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
  color: #721c24;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 8px;
  border-left: 4px solid #dc3545;
  font-weight: 500;
`;

const InventoryManagement = () => {
  const {
    loading,
    error,
    syncStatus,
    lowStockAlerts,
    expiringItems,
    loadInventoryData,
    checkSyncStatus
  } = useInventory();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAddModal, setShowAddModal] = useState(false);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'inventory', label: 'Inventory Items', icon: '📦' },
    { id: 'movements', label: 'Movement History', icon: '🔄' },
    { id: 'sync', label: 'Sync Status', icon: '🔗' }
  ];

  const handleRefresh = async () => {
    await Promise.all([
      loadInventoryData(),
      checkSyncStatus()
    ]);
  };

  const hasAlerts = lowStockAlerts.length > 0 || expiringItems.length > 0;

  return (
    <>
      {/* Status Bar */}
      <StatusBar inSync={syncStatus.inSync}>
        {syncStatus.inSync 
          ? `✅ System synchronized - Last sync: ${syncStatus.lastSync ? new Date(syncStatus.lastSync).toLocaleTimeString() : 'Never'}`
          : `⚠️ System out of sync - ${syncStatus.pendingOperations} pending operations`
        }
      </StatusBar>

      <Container style={{ marginTop: '50px' }}>
        {/* Loading Overlay */}
        {loading && (
          <LoadingOverlay>
            <Spinner />
          </LoadingOverlay>
        )}

        {/* Error Banner */}
        {error && (
          <AlertBanner>
            ⚠️ Error: {error}
          </AlertBanner>
        )}

        {/* Alerts Banner */}
        {hasAlerts && (
          <AlertBanner>
            🚨 {lowStockAlerts.length} low stock alerts, {expiringItems.length} items expiring soon
          </AlertBanner>
        )}

        {/* Header */}
        <Header>
          <Title>Inventory Management System</Title>
          <ActionButtons>
            <Button className="refresh" onClick={handleRefresh}>
              🔄 Refresh
            </Button>
            <Button 
              className="primary" 
              onClick={() => setShowAddModal(true)}
            >
              ➕ Add Inventory
            </Button>
          </ActionButtons>
        </Header>

        {/* Navigation Tabs */}
        <TabContainer>
          <TabList>
            {tabs.map(tab => (
              <Tab
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon} {tab.label}
              </Tab>
            ))}
          </TabList>

          {/* Tab Content */}
          <TabContent>
            {activeTab === 'dashboard' && <InventoryDashboard />}
            {activeTab === 'inventory' && <InventoryItemList />}
            {activeTab === 'movements' && <MovementHistory />}
            {activeTab === 'sync' && <SyncStatus />}
          </TabContent>
        </TabContainer>

        {/* Add Inventory Modal */}
        {showAddModal && (
          <AddInventoryModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
          />
        )}
      </Container>
    </>
  );
};

export default InventoryManagement; 